import React, {Component, FunctionComponent, MouseEvent, useRef, useState} from "react";
import {Button, Dimmer, Form, Icon, Input, Loader, Message, Modal, Segment, Table} from "semantic-ui-react";
import DeleteDialog from "../components/deleteDialog";
import {IItemDialogProps, IItemsListState, calcPagination, DialogType} from './globals';

interface ISoftware {
    id:             string,
    type:           string,
    version:        string,
    platform:       string,
    valid:          boolean,
    description:    string,
    cert:           string,
    upload:         number
}

const newSoftware = (): ISoftware => ({
    id:             "",
    type:           "",
    version:        "",
    platform:       "",
    valid:          true,
    description:    "",
    cert:           "",
    upload:         (new Date()).getTime()
});

const SoftwareEditDialog: FunctionComponent<IItemDialogProps<ISoftware>> = ({item, type, onClose}) => {
    const [data, setData] = useState(item);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [file, setFile] = useState("");

    const upload = useRef<HTMLInputElement>(null);

    const versionRegExp = /(\d+)\.(\d+)\.(\d+).(\d+)/;
    const publicKeyRegExp = /-----BEGIN PUBLIC KEY-----(.*)-----END PUBLIC KEY-----/s;

    const isValid = versionRegExp.test(data.version) &&
        publicKeyRegExp.test(data.cert) &&
        (type === "new" ? file.length : true) &&
        data.platform.length &&
        data.type.length;

    return (
        <Modal
            open
            closeIcon
            size="tiny"
            icon="edit"
            onClose={_ => onClose(false)}
        >
            <Modal.Header>{`${type === "new" ? "Create" : "Edit"} software`}</Modal.Header>
            <Modal.Content>
                <Dimmer active={loading} inverted>
                    <Loader>Process software info</Loader>
                </Dimmer>
                <Form>
                    <Form.Group>
                        <Form.Input width={14} error={!versionRegExp.test(data.version)} label="Version" placeholder="version" value={data.version} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({...data, version: e.target.value})}/>
                        <Form.Checkbox label="Valid" checked={data.valid} onChange={(e: React.FormEvent<HTMLInputElement>) => setData({...data, valid: !data.valid})}/>
                    </Form.Group>
                    <Form.Select
                        label="Platform"
                        options={[
                            {
                                text: "Windows",
                                icon: "windows",
                                value: "Windows"
                            },{
                                text: "MacOS",
                                icon: "apple",
                                value: "MacOS"
                            }
                        ]}
                        error={!data.platform.length}
                        value={data.platform} onChange={(e: React.SyntheticEvent<HTMLElement>, eventData) => setData({...data, platform: typeof eventData.value === "string" ? eventData.value : "" })}
                    />
                    <Form.Select
                        label="Purpose"
                        options={[
                            {
                                text: "Create",
                                value: "create"
                            },{
                                text: "Update",
                                value: "update"
                            }
                        ]}
                        error={!data.type.length}
                        value={data.type} onChange={(e: React.SyntheticEvent<HTMLElement>, eventData) => setData({...data, type: typeof eventData.value === "string" ? eventData.value : "" })}
                    />
                    <Form.TextArea label="Description" placeholder="Tell about software..." value={data.description} onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setData({...data, description: e.currentTarget.value})}/>
                    <Form.TextArea label="Public key" error={!publicKeyRegExp.test(data.cert)} placeholder="public key" value={data.cert} onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setData({...data, cert: e.currentTarget.value})}/>
                    <Form.Input readOnly label="Upload" value={new Date(data.upload).toLocaleString()} />
                    { type === "new" ? <Form.Group>
                        <input
                            type='file'
                            style={{display: 'none'}}
                            ref={upload}
                            onChange={_ => {
                                if (!(upload.current && upload.current.files))
                                    return;

                                setFile(upload.current.value);

                                // const formData = new FormData();
                                // formData.append('file', upload.current.files[0]);
                                //
                                // fetch("/software/check", {
                                //     method: "POST",
                                //     body: formData
                                // })
                                //     .then(result => result.ok
                                //         ? result.json().then(json => {
                                //             setData({...data, ...json});
                                //             setError("");
                                //         })
                                //         : result.text().then(error => setError(error)))
                                //     .catch(error => setError(error))
                            }}
                        />
                        <Form.Input width={10} readOnly error={file.length === 0} placeholder="Please select software File" value={file}/>
                        <Form.Button icon="upload" content="Upload Software" onClick={(e: React.MouseEvent<HTMLButtonElement>) => upload.current!.click()}/>
                    </Form.Group> : null}
                </Form>
                <Message negative hidden={error.length === 0}>
                    {error}
                </Message>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    positive
                    disabled={!isValid}
                    icon="save"
                    labelPosition="left"
                    content="Save"
                    onClick={(e: React.MouseEvent) => {
                        setLoading(true);

                        const init = type === "new"
                            ? {
                                method: "POST",
                                body: (_ => {
                                    const formData = new FormData();

                                    formData.append('data', JSON.stringify(data));
                                    if (upload.current && upload.current.files)
                                        formData.append('file', upload.current.files[0]);

                                    return formData;
                                })()
                            }
                            : {
                                method: "PUT",
                                headers: {'content-type' : 'application/json'},
                                body: JSON.stringify(data)
                            };

                        fetch("/software", init)
                            .then(result => result.ok
                                ? onClose(true)
                                : result.text()
                                    .then(error => setError(error)))
                            .catch(error => setError(error))
                            .finally(() => setLoading(false))
                    }}
                />
            </Modal.Actions>
        </Modal>
    )
}

export default class Software extends Component<{}, IItemsListState<ISoftware>> {
    state = {
        loading: false,
        items: [],
        page: 1,
        filter: "",
        order: "",
        desc: false,
        dialog: null
    }

    componentDidMount() {
        this.updateSoftware();
    }

    updateSoftware() {
        this.setState({loading: true});
        fetch("./software")
            .then(res => res.ok
                ? res.json().then((json) => this.setState({items: json}))
                : res.text().then(text => alert(text))
            )
            .finally(() => this.setState({loading: false}))
    }

    render() {
        if (this.state.loading)
            return (
                <Dimmer active inverted>
                    <Loader size='massive'>Loading</Loader>
                </Dimmer>
            );

        const pagination = calcPagination(this.state, 9, key => this.setState({page: key}));

        return (
            <Segment basic textAlign='right'>
                {this.state.dialog}

                <Button
                    floated='left'
                    icon="microchip"
                    content="Add Software"
                    onClick={(e: MouseEvent) => this.setState({dialog: <SoftwareEditDialog
                            item={newSoftware()}
                            type={DialogType.new}
                            onClose={(update:boolean) => {
                                this.setState({dialog: null})
                                update && this.updateSoftware();
                            }}
                        />
                    })}
                />

                <Input
                    floated='right'
                    icon={{ name: 'search', circular: true, link: true }}
                    placeholder='Search...'
                    onChange={(e, data) => this.setState({filter: data.value})}
                />

                <Table celled striped selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Valid</Table.HeaderCell>
                            <Table.HeaderCell>Type</Table.HeaderCell>
                            <Table.HeaderCell>Version</Table.HeaderCell>
                            <Table.HeaderCell>Platform</Table.HeaderCell>
                            <Table.HeaderCell>Description</Table.HeaderCell>
                            <Table.HeaderCell>Upload</Table.HeaderCell>
                            <Table.HeaderCell>Action</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {this.state.items.map((software: ISoftware, idx) =>  <Table.Row
                            key={idx}
                            className='customer-row'
                            onClick={(e: MouseEvent) => this.setState({dialog: <SoftwareEditDialog
                                    item={software}
                                    type={DialogType.edit}
                                    onClose={(update: boolean) => {
                                        this.setState({dialog: null})
                                        update && this.updateSoftware();
                                    }}
                                />
                            })}
                        >
                            <Table.Cell collapsing textAlign="center">{<Icon name={software.valid ? "check square outline" : "square outline"}/>}</Table.Cell>
                            <Table.Cell>{software.type === "create" ? "Creator" : "Updater"}</Table.Cell>
                            <Table.Cell>{software.version}</Table.Cell>
                            <Table.Cell>{software.platform}</Table.Cell>
                            <Table.Cell>{software.description}</Table.Cell>
                            <Table.Cell collapsing>{new Date(software.upload).toLocaleString()}</Table.Cell>
                            <Table.Cell collapsing textAlign="center" className="customer-action-cell" onClick={(e: MouseEvent) => e.stopPropagation()}>
                                <Icon
                                    link
                                    name="download"
                                    onClick={(e: MouseEvent) => window.open(`/software/${software.id}`,'_self')}
                                />
                                <Icon
                                    link
                                    name="remove"
                                    onClick={(e: MouseEvent) => {
                                        e.stopPropagation();
                                        this.setState({dialog: <DeleteDialog
                                                id={software.id}
                                                label={software.version}
                                                uri="software"
                                                onClose={(update: boolean) => {
                                                    this.setState({dialog: null})
                                                    update && this.updateSoftware();
                                                }}
                                            />
                                        })
                                    }}
                                />
                            </Table.Cell>
                        </Table.Row>)}
                    </Table.Body>
                </Table>
            </Segment>
        )
    }
}